import { createFileRoute } from "@tanstack/react-router";
import {
	Calendar,
	ExternalLink,
	FileSpreadsheet,
	FileText,
	Paperclip,
} from "lucide-react";

import { useLang } from "@/contexts/lang";
import {
	getAttachmentType,
	getAttachmentUrl,
	getInvestorAnnouncements,
	type InvestorAnnouncement,
} from "@/lib/investorAnnouncements";

export const Route = createFileRoute("/investitori")({
	ssr: false,
	loader: async () => {
		const announcements = await getInvestorAnnouncements("mk-MK");
		return { announcements };
	},
	head: () => ({
		meta: [
			{ title: "Инвеститори | Македонијатурист АД" },
			{
				name: "description",
				content:
					"Портал за инвеститори на Македонијатурист АД. Пристап до финансиски извештаи, деловни планови и важни информации за акционерите.",
			},
			{
				name: "robots",
				content: "noindex, nofollow",
			},
		],
		links: [
			{
				rel: "canonical",
				href: "https://makedonijaturist.com.mk/investitori",
			},
		],
	}),
	component: InvestitoriPage,
});

export function InvestitoriPage() {
	const { announcements } = Route.useLoaderData();
	return <InvestitoriPageContent announcements={announcements} />;
}

export function InvestitoriPageContent({
	announcements,
}: {
	announcements: InvestorAnnouncement[];
}) {
	const t = useLang();

	return (
		<main className="mt-grain bg-background">
			{/* Public Announcements */}
			<section className="bg-background py-12 sm:py-16 md:py-24">
				<div className="mx-auto max-w-7xl px-4 sm:px-6">
					<div className="mb-10 sm:mb-14">
						<span className="inline-block h-1 w-12 bg-accent mb-4 sm:mb-6" />
						<h1 className="font-display text-2xl sm:text-3xl md:text-4xl font-medium tracking-tight text-primary leading-[1.1]">
							{t.investitoriPage.announcementsHeading}
						</h1>
						<p className="mt-4 text-base text-foreground/70 font-light leading-relaxed max-w-2xl">
							{t.investitoriPage.announcementsDesc}
						</p>
					</div>

					<div className="grid gap-6">
						{announcements.map((announcement) => (
							<div
								key={announcement.documentId}
								className="rounded-xl border border-border bg-card p-6 sm:p-8 shadow-sm"
							>
								<div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
									<h3 className="font-display text-lg sm:text-xl font-medium text-primary leading-snug">
										{announcement.title}
									</h3>
									<span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground shrink-0 sm:mt-1">
										<Calendar className="h-3.5 w-3.5" />
										<span className="font-medium">
											{t.investitoriPage.publishedLabel}:
										</span>
										{announcement.date}
									</span>
								</div>

								{announcement.attachments &&
									announcement.attachments.length > 0 && (
										<div className="mt-6">
											<div className="flex items-center gap-2 mb-4">
												<Paperclip className="h-3.5 w-3.5 text-accent" />
												<span className="text-xs font-bold tracking-[0.15em] uppercase text-muted-foreground">
													{t.investitoriPage.attachmentsLabel}
												</span>
											</div>
											<div className="grid gap-3 sm:gap-3 md:grid-cols-2">
												{announcement.attachments.map((att) => {
													const fileUrl = getAttachmentUrl(att.file.url);
													const fileType = getAttachmentType(
														att.file.url,
														att.file.ext,
													);
													return (
														<a
															key={att.id}
															href={fileUrl}
															target="_blank"
															rel="noopener noreferrer"
															className="group flex items-start gap-4 rounded-lg border border-border bg-background p-4 transition-all hover:-translate-y-0.5 hover:border-accent/30 hover:shadow-md"
														>
															<div className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/5 text-primary">
																{fileType === "XLSX" ? (
																	<FileSpreadsheet className="h-4 w-4" />
																) : (
																	<FileText className="h-4 w-4" />
																)}
															</div>
															<div className="flex-1 min-w-0">
																<div className="flex items-start justify-between gap-2">
																	<span className="text-sm font-medium text-primary leading-snug">
																		{att.name}
																	</span>
																	<span className="shrink-0 rounded bg-secondary px-2 py-0.5 text-[10px] font-bold tracking-[0.15em] uppercase text-muted-foreground">
																		{fileType}
																	</span>
																</div>
																<span className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold tracking-[0.12em] uppercase text-primary/50 group-hover:text-primary transition-colors duration-300">
																	<ExternalLink className="h-3.5 w-3.5" />
																	{t.investitoriPage.open}
																</span>
															</div>
														</a>
													);
												})}
											</div>
										</div>
									)}
							</div>
						))}
					</div>
				</div>
			</section>
		</main>
	);
}
